/**
 * Bilingual content management utilities
 */
import { useTranslation } from 'react-i18next';

/**
 * Get content in the current language
 * @param {Object} bilingualContent - Object with 'en' and 'jp' properties
 * @param {string} currentLanguage - Current language ('en' or 'jp')
 * @returns {string} Content in current language
 */
export function getBilingualContent(bilingualContent, currentLanguage = 'en') {
  if (!bilingualContent || typeof bilingualContent !== 'object') {
    return String(bilingualContent || '');
  }

  // Use current language or fall back to English
  return bilingualContent[currentLanguage] || bilingualContent.en || bilingualContent.jp || '';
}

/**
 * Create a bilingual content object
 * @param {string} englishText - English text
 * @param {string} japaneseText - Japanese text
 * @returns {Object} Bilingual content object
 */
export function createBilingualContent(englishText, japaneseText) {
  return {
    en: englishText || '',
    jp: japaneseText || ''
  };
}

/**
 * Merge bilingual content objects
 * @param {Object} primary - Primary bilingual content
 * @param {Object} fallback - Fallback bilingual content
 * @returns {Object} Merged bilingual content
 */
export function mergeBilingualContent(primary, fallback) {
  return {
    en: primary?.en || fallback?.en || '',
    jp: primary?.jp || fallback?.jp || ''
  };
}

/**
 * Hook for getting bilingual content with translation support
 * @returns {Object} Functions for handling bilingual content
 */
export function useBilingualContent() {
  const { i18n } = useTranslation();
  const currentLang = i18n.language === 'jp' ? 'jp' : 'en';

  return {
    /**
     * Get content in current language
     * @param {Object} content - Bilingual content object
     * @returns {string} Content in current language
     */
    getText: (content) => getBilingualContent(content, currentLang),

    /**
     * Get both languages for display
     * @param {Object} content - Bilingual content object
     * @returns {Object} Object with current and other language text
     */
    getBothLanguages: (content) => {
      const otherLang = currentLang === 'en' ? 'jp' : 'en';
      return {
        primary: getBilingualContent(content, currentLang),
        secondary: getBilingualContent(content, otherLang),
        primaryLang: currentLang,
        secondaryLang: otherLang
      };
    },

    /**
     * Current language
     */
    currentLang,

    /**
     * Switch language
     */
    switchLanguage: () => {
      const newLang = currentLang === 'en' ? 'jp' : 'en';
      i18n.changeLanguage(newLang);
    }
  };
}

/**
 * Convert legacy data to bilingual format
 * @param {Object} person - Person data from people.json
 * @returns {Object} Person data with bilingual content
 */
export function convertPersonToBilingual(person) {
  return {
    ...person,
    name: createBilingualContent(person.name_romaji, person.name_jp),
    biography: createBilingualContent(
      generateEnglishBiography(person),
      person.description
    )
  };
}

/**
 * Convert item name to bilingual format
 * @param {string} itemName - Japanese item name
 * @returns {Object} Bilingual item name
 */
export function convertItemNameToBilingual(itemName) {
  return createBilingualContent(
    translateItemNameToEnglish(itemName),
    itemName
  );
}

/**
 * Generate basic English biography from person data
 * @param {Object} person - Person data
 * @returns {string} English biography
 */
function generateEnglishBiography(person) {
  let bio = `${person.name_romaji} (${person.years}) was a historical figure from ${person.region}. `;

  // Add context based on description keywords
  const description = person.description || '';
  if (description.includes('茶人') || description.includes('茶道')) {
    bio += 'They were involved in the Japanese tea ceremony. ';
  }
  if (description.includes('哲学者')) {
    bio += 'They were a philosopher. ';
  }
  if (description.includes('実業家')) {
    bio += 'They were a businessman. ';
  }
  if (description.includes('陶芸家')) {
    bio += 'They were a potter. ';
  }
  if (description.includes('大名') || description.includes('武将')) {
    bio += 'They were a feudal lord or military leader. ';
  }

  bio += 'More details available from historical sources.';
  return bio;
}

/**
 * Basic item name translation
 * @param {string} itemName - Japanese item name
 * @returns {string} English item name
 */
function translateItemNameToEnglish(itemName) {
  const translations = {
    '茶碗': 'Tea bowl',
    '茶杓': 'Tea scoop',
    '花入': 'Flower vase',
    '香合': 'Incense container',
    '水指': 'Water jar',
    '釜': 'Kettle',
    '風炉': 'Brazier',
    '掛物': 'Hanging scroll',
    '掛軸': 'Hanging scroll',
    '羽箒': 'Feather brush',
    '薄器': 'Thin tea container',
    '棗': 'Tea caddy',
    '蓋置': 'Lid rest',
    '建水': 'Waste water bowl',
    '竹': 'bamboo',
    '青磁': 'celadon',
    '織部': 'Oribe',
    '志野': 'Shino',
    '唐津': 'Karatsu',
    '樂': 'Raku'
  };

  let englishName = itemName;
  for (const [jp, en] of Object.entries(translations)) {
    englishName = englishName.replace(new RegExp(jp, 'g'), en);
  }

  return englishName.trim();
}

/**
 * Format years for display in current language
 * @param {number} birthYear - Birth year
 * @param {number} deathYear - Death year
 * @param {string} lang - Language ('en' or 'jp')
 * @returns {string} Formatted years string
 */
export function formatYearsForLanguage(birthYear, deathYear, lang = 'en') {
  if (!birthYear && !deathYear) {
    return lang === 'jp' ? '年代不詳' : 'Dates unknown';
  }

  if (birthYear && deathYear) {
    return lang === 'jp'
      ? `${Math.abs(birthYear)}${birthYear < 0 ? '年 BCE' : '年'} - ${Math.abs(deathYear)}${deathYear < 0 ? '年 BCE' : '年'}`
      : `${Math.abs(birthYear)}${birthYear < 0 ? ' BCE' : ''} - ${Math.abs(deathYear)}${deathYear < 0 ? ' BCE' : ''}`;
  }

  if (birthYear) {
    return lang === 'jp'
      ? `${Math.abs(birthYear)}${birthYear < 0 ? '年 BCE' : '年'}生`
      : `Born ${Math.abs(birthYear)}${birthYear < 0 ? ' BCE' : ''}`;
  }

  if (deathYear) {
    return lang === 'jp'
      ? `${Math.abs(deathYear)}${deathYear < 0 ? '年 BCE' : '年'}没`
      : `Died ${Math.abs(deathYear)}${deathYear < 0 ? ' BCE' : ''}`;
  }

  return '';
}